import React, { useState, useEffect, useMemo } from "react";
import { XMarkIcon, PlusIcon, TrashIcon } from "@heroicons/react/20/solid";
import SearchableDropdown from "../DynamicList/SearchableDropdown";

const ExportCSVModal = ({ isOpen, onClose, columns, onExport }) => {
  const [selectedFields, setSelectedFields] = useState([]);
  const [isExporting, setIsExporting] = useState(false);

  // Memoize field options to prevent infinite loops
  const fieldOptions = useMemo(() => {
    return columns
      .filter((c) => !c.hide || c.sendToExport === true)
      .map((c) => ({
        value: c.field,
        label: c.label,
        field: c.field,
        concat: c.concat || null,
        subvalue: c.subvalue || c.relationSubvalue || null,
        isRelation: c.isRelation || null,
        relationWith: c.relationWith || null,
        where: c.where || null,
        isArray: c.isArray || false,
        showValue: c.showValue || false,
        isDate: c.isDate || false,
        branchCode: c.branchCode || null,
      }));
  }, [columns]);

  // Pre-select all fields when modal opens
  useEffect(() => {
    if (isOpen && fieldOptions.length > 0) {
      const allFields = fieldOptions.map((option) => ({
        field: option.field,
        label: option.label,
        value: option.value,
        concat: option.concat,
        subvalue: option.subvalue,
        isRelation: option.isRelation,
        relationWith: option.relationWith,
        where: option.where,
        isArray: option.isArray,
        showValue: option.showValue,
        isDate: option.isDate,
        branchCode: option.branchCode,
      }));
      setSelectedFields(allFields);
    }
  }, [isOpen, fieldOptions]);

  const addField = () => {
    setSelectedFields([...selectedFields, { field: "", label: "" }]);
  };

  const removeField = (index) => {
    const newFields = selectedFields.filter((_, i) => i !== index);
    setSelectedFields(newFields);
  };

  const updateField = (index, selectedOption) => {
    const newFields = [...selectedFields];
    if (selectedOption) {
      newFields[index] = {
        field: selectedOption.field,
        label: selectedOption.label,
        value: selectedOption.value,
        concat: selectedOption.concat,
        subvalue: selectedOption.subvalue,
        isRelation: selectedOption.isRelation,
        relationWith: selectedOption.relationWith,
        where: selectedOption.where,
        isArray: selectedOption.isArray,
        showValue: selectedOption.showValue,
        isDate: selectedOption.isDate,
        branchCode: selectedOption.branchCode,
      };
    } else {
      newFields[index] = { field: "", label: "" };
    }
    setSelectedFields(newFields);
  };

  const handleExport = async () => {
    // Filter out empty fields
    const validFields = selectedFields.filter(
      (field) => field.field && field.label
    );

    if (validFields.length === 0) {
      alert("Please select at least one field to export.");
      return;
    }

    // Set loading state
    setIsExporting(true);

    try {
      // Prepare export data
      const exportData = {
        fields: validFields,
        timestamp: new Date().toISOString(),
      };

      // Call the parent export handler
      await onExport(exportData);

      // Reset and close modal
      setSelectedFields([]);
      onClose();
    } catch (error) {
      console.error("Export failed:", error);
      alert("Export failed. Please try again.");
    } finally {
      // Reset loading state
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    // Don't allow closing while exporting
    if (isExporting) return;

    setSelectedFields([]);
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4">
        {/* Modal Header */}
        <div className="flex items-center justify-between p-6 border-b">
          <h3 className="text-lg font-semibold text-gray-900">Export CSV</h3>
          <button
            onClick={handleClose}
            className={`text-gray-400 hover:text-gray-600 ${
              isExporting ? "cursor-not-allowed opacity-50" : ""
            }`}
            disabled={isExporting}
          >
            <XMarkIcon className="h-6 w-6" />
          </button>
        </div>

        {/* Modal Body */}
        <div className="p-6 space-y-4">
          {/* Fields Section */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Fields to Export *
            </label>

            {/* Selected Fields */}
            <div className="space-y-3 mb-4 max-h-60 overflow-y-auto">
              {selectedFields.map((field, index) => (
                <div key={index} className="flex items-center gap-2">
                  <div className="flex-1">
                    <SearchableDropdown
                      options={fieldOptions.filter(
                        (option) =>
                          !selectedFields.some(
                            (f, i) => i !== index && f.value === option.value
                          )
                      )}
                      value={field.label}
                      onChange={(selectedOption) =>
                        updateField(index, selectedOption)
                      }
                      placeholder="Select Field"
                      disabled={isExporting}
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeField(index)}
                    className={`p-2 text-red-500 hover:text-red-700 ${
                      isExporting ? "cursor-not-allowed opacity-50" : ""
                    }`}
                    title="Remove field"
                    disabled={isExporting}
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              ))}
            </div>

            {/* Add Field Button - only show if there are available fields */}
            {selectedFields.length < fieldOptions.length && (
              <button
                type="button"
                onClick={addField}
                className={`inline-flex items-center gap-2 text-sm text-blue-600 hover:text-blue-700 hover:underline ${
                  isExporting ? "cursor-not-allowed opacity-50" : ""
                }`}
                disabled={isExporting}
              >
                <PlusIcon className="h-4 w-4" />
                Add Field
              </button>
            )}

            {selectedFields.length >= fieldOptions.length && (
              <p className="text-xs text-gray-500 mt-1">
                All available fields are selected.
              </p>
            )}
          </div>

          {/* Selected Fields Preview */}
          {selectedFields.length > 0 && (
            <div className="bg-gray-50 rounded-md p-3">
              <h4 className="text-sm font-medium text-gray-700 mb-2">
                Export Preview ({selectedFields.filter((f) => f.label).length}{" "}
                fields)
              </h4>
              <div className="flex flex-wrap gap-2">
                {selectedFields
                  .filter((field) => field.label)
                  .map((field, index) => (
                    <span
                      key={index}
                      className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800"
                    >
                      {field.label}
                    </span>
                  ))}
              </div>
            </div>
          )}

          {/* Loading indicator during export */}
          {isExporting && (
            <div className="bg-blue-50 border border-blue-200 rounded-md p-3">
              <div className="flex items-center gap-3">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="animate-spin h-5 w-5 text-blue-600"
                  viewBox="0 0 24 24"
                  strokeWidth={1.5}
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                </svg>
                <span className="text-sm text-blue-700 font-medium">
                  Exporting your data...
                </span>
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer */}
        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
          <button
            type="button"
            onClick={handleClose}
            className={`px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
              isExporting ? "cursor-not-allowed opacity-50" : ""
            }`}
            disabled={isExporting}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleExport}
            className={`px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 inline-flex items-center gap-2 ${
              isExporting || selectedFields.filter((f) => f.label).length === 0
                ? "cursor-not-allowed opacity-50"
                : ""
            }`}
            disabled={
              isExporting || selectedFields.filter((f) => f.label).length === 0
            }
          >
            {isExporting && (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="animate-spin h-4 w-4"
                viewBox="0 0 24 24"
                strokeWidth={1.5}
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            )}
            {isExporting ? "Exporting..." : "Export CSV"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExportCSVModal;
